import { StyleSheet } from 'aphrodite';
import { THEME } from '../../../web/components-v2/Theme/theme';

const LIST_PANEL_WIDTH = 320;

export const styleSheet = StyleSheet.create({
	// The page renders on a bare route (no MainContainer chrome), so it owns the full-height column:
	// the top bar sits above and this flex parent lets the body below fill the rest of the viewport.
	page: {
		display: 'flex',
		flexDirection: 'column',
		height: '100vh',
		minHeight: 0,
	},
	layout: {
		display: 'flex',
		flex: 1,
		gap: THEME.spacing.md,
		// Fill the page column below the top bar and clip our own overflow, so the left list and the
		// right transcript each scroll INTERNALLY and independently — instead of the whole page
		// scrolling as one (which dragged the list along with the conversation).
		height: '100%',
		minHeight: 0,
		overflow: 'hidden',
		padding: THEME.spacing.md,
		[THEME.queries.md]: {
			// Stacked on mobile: let the body grow and scroll as one column again.
			flexDirection: 'column',
			gap: THEME.spacing.sm,
			height: 'auto',
			overflow: 'auto',
		},
	},
	panel: {
		backgroundColor: THEME.colors.background.light,
		border: THEME.common.border,
		borderRadius: THEME.common.borderRadius,
		boxSizing: 'border-box',
		display: 'flex',
		flexDirection: 'column',
		minHeight: 0,
		overflow: 'hidden',
	},
	cardsPane: {
		display: 'flex',
		flex: 1,
		minHeight: 0,
		minWidth: 0,
	},
	// Opening a call hides the grid rather than unmounting it: the cards are what poll for new calls,
	// and a remount would re-baseline them, silently swallowing anything that landed while reading.
	cardsPaneHidden: {
		display: 'none',
	},
	listPanel: {
		flexShrink: 0,
		width: LIST_PANEL_WIDTH,
		[THEME.queries.md]: {
			width: '100%',
		},
	},
	detailPanel: {
		flex: 1,
		// The detail column scrolls as ONE unit (adherence → transcript),
		// independent of the left list. `panel` sets overflow:hidden; override the vertical axis here.
		overflowX: 'hidden',
		overflowY: 'auto',
	},
	// Shown in place of the detail panels when a `?convo=<id>` deep link resolves to nothing (404).
	detailNotFound: {
		alignItems: 'center',
		color: THEME.colors.text.light,
		display: 'flex',
		flex: 1,
		fontSize: THEME.typography.fontSizes.sm,
		justifyContent: 'center',
		padding: THEME.spacing.xl,
		textAlign: 'center',
	},
	panelHeader: {
		alignItems: 'center',
		backgroundColor: THEME.colors.background.main,
		borderBottom: THEME.common.border,
		display: 'flex',
		gap: THEME.spacing.sm,
		justifyContent: 'space-between',
		padding: `${THEME.spacing.md}px ${THEME.spacing.lg}px`,
	},
	panelTitle: {
		color: THEME.colors.text.dark,
		fontSize: THEME.typography.fontSizes.lg,
		fontWeight: THEME.typography.fontWeights.bold,
		margin: 0,
	},
	panelBody: {
		alignItems: 'center',
		color: THEME.colors.text.light,
		display: 'flex',
		flex: 1,
		fontSize: THEME.typography.fontSizes.sm,
		justifyContent: 'center',
		minHeight: 0,
		padding: THEME.spacing.lg,
		textAlign: 'center',
	},
	listBody: {
		display: 'flex',
		flex: 1,
		flexDirection: 'column',
		minHeight: 0,
		overflowY: 'auto',
	},
	filters: {
		borderBottom: THEME.common.border,
		display: 'flex',
		flexDirection: 'column',
		flexShrink: 0,
		gap: THEME.spacing.sm,
		padding: `${THEME.spacing.md}px ${THEME.spacing.lg}px`,
	},
	filterDateRange: {
		display: 'flex',
		gap: THEME.spacing.sm,
	},
	filterAgent: {
		// The picker's control is width:100%, so it needs a block parent to fill like its siblings.
		display: 'block',
	},
	repSelectTrigger: {
		// Match the full-width search Input (the Select trigger defaults to fit-content).
		width: '100%',
	},
	detailBody: {
		// Let the transcript flow at its natural height so the whole detail column scrolls as one
		// (the parent `detailPanel` owns the scrollbar). NOT flex:1 — otherwise the transcript becomes
		// a bounded internal scroll window and squeezes the rest of the column off-screen (the bug
		// where the right pane couldn't be scrolled).
		display: 'flex',
	},
	thread: {
		backgroundColor: THEME.colors.background.light,
		flex: 1,
		paddingLeft: THEME.spacing.lg,
		[THEME.queries.md]: {
			paddingLeft: THEME.spacing.md,
		},
	},
	threadContent: {
		paddingBottom: THEME.spacing.lg,
		paddingRight: THEME.spacing.lg,
		paddingTop: THEME.spacing.md,
		[THEME.queries.md]: {
			paddingRight: THEME.spacing.md,
		},
	},
	listState: {
		alignItems: 'center',
		color: THEME.colors.text.light,
		display: 'flex',
		flex: 1,
		fontSize: THEME.typography.fontSizes.sm,
		justifyContent: 'center',
		minHeight: 0,
		padding: THEME.spacing.lg,
		textAlign: 'center',
	},
	spinnerState: {
		alignItems: 'center',
		display: 'flex',
		flex: 1,
		justifyContent: 'center',
		minHeight: 240,
	},
	// Override the Button (variant='ghost') base style — which centers content, uses an 8px
	// borderRadius, and a fixed 36px height — so this row renders as a full-width, left-aligned,
	// multi-line list item instead of a centered, fixed-height button.
	listItem: {
		alignItems: 'flex-start',
		background: 'transparent',
		border: 'none',
		borderBottom: THEME.common.border,
		borderRadius: 0,
		cursor: 'pointer',
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.xs,
		height: 'auto',
		justifyContent: 'flex-start',
		padding: `${THEME.spacing.md}px ${THEME.spacing.lg}px`,
		textAlign: 'left',
		width: '100%',
		':hover': {
			backgroundColor: THEME.colors.hover.light,
		},
	},
	listItemSelected: {
		backgroundColor: THEME.colors.hover.light,
		boxShadow: `inset 3px 0 0 ${THEME.colors.primary.main}`,
		':hover': {
			backgroundColor: THEME.colors.hover.light,
		},
	},
	listItemTitle: {
		color: THEME.colors.text.dark,
		fontSize: THEME.typography.fontSizes.sm,
		fontWeight: THEME.typography.fontWeights.bold,
	},
	// "Lead · Company" line under the rep name — who the coaching call was with, for scanning the list.
	listItemSubject: {
		alignItems: 'center',
		display: 'flex',
		flexWrap: 'wrap',
		fontSize: THEME.typography.fontSizes.xs,
		gap: THEME.spacing.xs,
		maxWidth: '100%',
	},
	listItemLead: {
		...THEME.common.truncate,
		color: THEME.colors.text.main,
		fontWeight: THEME.typography.fontWeights.semibold,
		maxWidth: '100%',
	},
	listItemSubjectSep: {
		color: THEME.colors.text.light,
	},
	listItemCompany: {
		...THEME.common.truncate,
		color: THEME.colors.text.light,
		maxWidth: '100%',
	},
	listItemMeta: {
		alignItems: 'center',
		color: THEME.colors.text.light,
		display: 'flex',
		fontSize: THEME.typography.fontSizes.xs,
		gap: THEME.spacing.sm,
		justifyContent: 'space-between',
	},
	// Connected talk time, right-aligned on the meta row. Tabular figures keep the m:ss aligned
	// down the column so longer calls are easy to spot at a glance.
	listItemDuration: {
		color: THEME.colors.text.main,
		flexShrink: 0,
		fontVariantNumeric: 'tabular-nums',
		fontWeight: THEME.typography.fontWeights.semibold,
	},
	// Row of compact chips (outcome + UI version + agent) under the rep/date, for scanning the list.
	listItemTags: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: THEME.spacing.xs,
		marginTop: THEME.spacing.xs,
	},
	// Bottom-of-list "Load more" control + loaded/total caption. Sits after the rows inside the
	// scrollable list body so it follows the last conversation.
	listFooter: {
		alignItems: 'center',
		borderTop: THEME.common.border,
		display: 'flex',
		flexDirection: 'column',
		flexShrink: 0,
		gap: THEME.spacing.sm,
		padding: `${THEME.spacing.md}px ${THEME.spacing.lg}px`,
	},
	loadMoreButton: {
		width: '100%',
	},
	listCount: {
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.xs,
	},
	adherence: {
		borderBottom: THEME.common.border,
		flexShrink: 0,
	},
	adherenceBody: {
		padding: `${THEME.space[16]}px ${THEME.space[24]}px`,
	},
	adherenceEmpty: {
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.sm,
	},
	metadata: {
		borderBottom: THEME.common.border,
		flexShrink: 0,
	},
	metadataHeader: {
		alignItems: 'center',
		backgroundColor: THEME.colors.background.main,
		display: 'flex',
		gap: THEME.spacing.sm,
		justifyContent: 'space-between',
		padding: `${THEME.spacing.sm}px ${THEME.spacing.lg}px`,
	},
	metadataTitle: {
		color: THEME.colors.text.dark,
		fontSize: THEME.typography.fontSizes.sm,
		fontWeight: THEME.typography.fontWeights.bold,
		margin: 0,
	},
	metadataBody: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.md,
		margin: 0,
		padding: `${THEME.spacing.md}px ${THEME.spacing.lg}px`,
	},
	metaTop: {
		alignItems: 'center',
		display: 'flex',
		gap: THEME.spacing.sm,
	},
	// Sizes the shared Avatar down from its 60px default to fit the metadata header.
	metaAvatar: {
		flexShrink: 0,
		height: 36,
		width: 36,
	},
	metaIdentity: {
		alignItems: 'flex-start',
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.xs,
		minWidth: 0,
	},
	metaRep: {
		...THEME.common.truncate,
		color: THEME.colors.text.dark,
		fontSize: THEME.typography.fontSizes.sm,
		fontWeight: THEME.typography.fontWeights.bold,
		maxWidth: '100%',
	},
	metaFeedback: {
		display: 'flex',
		gap: THEME.spacing.sm,
		marginLeft: 'auto',
	},
	fbChip: {
		alignItems: 'center',
		borderRadius: 9999,
		display: 'inline-flex',
		fontSize: THEME.typography.fontSizes.sm,
		fontWeight: THEME.typography.fontWeights.semibold,
		gap: THEME.spacing.xs,
		padding: `2px ${THEME.spacing.sm}px`,
	},
	fbChipUp: {
		backgroundColor: THEME.colors.pill.green.background,
		color: THEME.colors.pill.green.icon,
	},
	fbChipDown: {
		backgroundColor: THEME.colors.pill.red.background,
		color: THEME.colors.pill.red.icon,
	},
	metaStats: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: THEME.spacing.sm,
		margin: 0,
	},
	metaStat: {
		backgroundColor: THEME.colors.background.light,
		border: `1px solid ${THEME.colors.border.light}`,
		borderRadius: THEME.common.borderRadius,
		flex: '1 1 130px',
		minWidth: 120,
		padding: `${THEME.spacing.sm}px ${THEME.spacing.md}px`,
	},
	metaStatLabel: {
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.xxs,
		fontWeight: THEME.typography.fontWeights.bold,
		letterSpacing: '0.04em',
		textTransform: 'uppercase',
	},
	metaStatValue: {
		color: THEME.colors.text.main,
		fontSize: THEME.typography.fontSizes.sm,
		lineHeight: 1.3,
		margin: 0,
		marginTop: THEME.spacing.xs,
	},
	metaIdRow: {
		alignItems: 'center',
		borderTop: `1px solid ${THEME.colors.border.light}`,
		display: 'flex',
		flexWrap: 'wrap',
		gap: THEME.spacing.sm,
		paddingTop: THEME.spacing.md,
	},
	// Sized to its content (not flex: 1) so the id chip hugs the value instead of filling the row.
	metaIdValue: {
		...THEME.common.truncate,
		backgroundColor: THEME.colors.background.main,
		border: `1px solid ${THEME.colors.border.light}`,
		borderRadius: THEME.common.borderRadius,
		color: THEME.colors.text.main,
		flexShrink: 1,
		fontFamily: 'monospace',
		fontSize: THEME.typography.fontSizes.xs,
		maxWidth: '100%',
		minWidth: 0,
		padding: `3px ${THEME.spacing.sm}px`,
	},
	metaIdCopy: {
		flexShrink: 0,
		gap: THEME.spacing.xs,
		marginLeft: 'auto',
	},
	metaIdCopyIcon: {
		height: 14,
		width: 14,
	},
	insights: {
		borderBottom: THEME.common.border,
		flexShrink: 0,
	},
	insightsHeader: {
		alignItems: 'center',
		backgroundColor: THEME.colors.background.main,
		display: 'flex',
		gap: THEME.spacing.sm,
		justifyContent: 'space-between',
		padding: `${THEME.spacing.sm}px ${THEME.spacing.lg}px`,
	},
	insightsTitle: {
		color: THEME.colors.text.dark,
		fontSize: THEME.typography.fontSizes.sm,
		fontWeight: THEME.typography.fontWeights.bold,
		margin: 0,
	},
	insightsBody: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.lg,
		padding: `${THEME.spacing.md}px ${THEME.spacing.lg}px`,
	},
	insightsChartTitle: {
		color: THEME.colors.text.main,
		fontSize: THEME.typography.fontSizes.xs,
		fontWeight: THEME.typography.fontWeights.semibold,
		margin: 0,
		marginBottom: THEME.spacing.sm,
	},
	// Collapsible trigger chevron — points down when collapsed, flips up (180deg) when open.
	collapseChevron: {
		color: THEME.colors.text.light,
		transition: 'transform 0.15s ease-out',
	},
	collapseChevronOpen: {
		transform: 'rotate(180deg)',
	},
	insightsSection: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.md,
	},
	// Compact, content-sized stat chips for the Insights summary (short values like "15s", "3").
	insightsStats: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: THEME.spacing.sm,
		margin: 0,
	},
	insightsStat: {
		backgroundColor: THEME.colors.background.light,
		border: `1px solid ${THEME.colors.border.light}`,
		borderRadius: THEME.common.borderRadius,
		flex: '0 1 auto',
		padding: `${THEME.spacing.xs}px ${THEME.spacing.sm}px`,
	},
	insightsStatLabel: {
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.xxs,
		fontWeight: THEME.typography.fontWeights.bold,
		letterSpacing: '0.04em',
		textTransform: 'uppercase',
	},
	insightsStatValue: {
		color: THEME.colors.text.dark,
		fontSize: THEME.typography.fontSizes.sm,
		fontWeight: THEME.typography.fontWeights.bold,
		lineHeight: 1.2,
		margin: 0,
		marginTop: 2,
	},
	insightsChartSuffix: {
		color: THEME.colors.text.light,
		fontWeight: THEME.typography.fontWeights.standard,
	},
	turnGapChart: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.md,
	},
	turnGapRow: {
		alignItems: 'center',
		display: 'flex',
		gap: THEME.spacing.md,
	},
	turnGapLabel: {
		color: THEME.colors.text.light,
		flexShrink: 0,
		fontSize: THEME.typography.fontSizes.xs,
		lineHeight: 1.2,
		textAlign: 'right',
		width: 132,
	},
	turnGapTrack: {
		alignItems: 'center',
		backgroundColor: THEME.colors.background.main,
		borderRadius: THEME.common.borderRadius,
		display: 'flex',
		flex: 1,
		height: 24,
		minWidth: 0,
		overflow: 'visible',
	},
	turnGapBar: {
		borderRadius: THEME.common.borderRadius,
		flexShrink: 0,
		height: 24,
		minWidth: 6,
		transition: 'width 0.35s cubic-bezier(0.22, 1, 0.36, 1)',
	},
	turnGapValue: {
		color: THEME.colors.text.main,
		flexShrink: 0,
		fontSize: THEME.typography.fontSizes.xs,
		fontWeight: THEME.typography.fontWeights.bold,
		marginLeft: THEME.spacing.sm,
		whiteSpace: 'nowrap',
	},
	insightsSpinner: {
		alignItems: 'center',
		display: 'flex',
		justifyContent: 'center',
		minHeight: 80,
	},
	insightsNote: {
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.xs,
		fontStyle: 'italic',
		margin: 0,
	},
	insightsTooltip: {
		backgroundColor: THEME.colors.background.light,
		border: THEME.common.border,
		borderRadius: THEME.common.borderRadius,
		boxShadow: `0 2px 8px ${THEME.colors.border.main}`,
		fontSize: THEME.typography.fontSizes.xs,
		padding: `${THEME.spacing.sm}px ${THEME.spacing.md}px`,
	},
	insightsTooltipTitle: {
		color: THEME.colors.text.dark,
		fontWeight: THEME.typography.fontWeights.bold,
		marginBottom: THEME.spacing.xs,
	},
	insightsTooltipLine: {
		color: THEME.colors.text.main,
		margin: 0,
	},
});
